import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../common/LoadingSpinner';
import GymLogo from '../layout/GymLogo';
import { DASHBOARD_BG_DARK, DASHBOARD_BG_LIGHT } from '../../constants/dashboardBackgrounds';

const inputClass =
    'px-4 py-3 w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#372d83]/40 focus:border-[#372d83] outline-none transition-shadow';

export default function GymFirstLoginSetupPage() {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const { getAuthenticatedApi, refreshCurrentUser } = useAuth();
    const navigate = useNavigate();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const shellStyle = { background: isDark ? DASHBOARD_BG_DARK : DASHBOARD_BG_LIGHT };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError(t('gym.firstLoginSetup.passwordTooShort'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('gym.firstLoginSetup.passwordMismatch'));
            return;
        }
        if (newPassword.startsWith('TEMP_')) {
            setError(t('gym.firstLoginSetup.passwordTempNotAllowed'));
            return;
        }

        setLoading(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.post(
                '/password_reset_inapp.php?action=change_password',
                { new_password: newPassword }
            );
            if (response.data?.success) {
                await refreshCurrentUser();
                navigate('/gym', { replace: true });
            } else {
                setError(response.data?.error || t('gym.firstLoginSetup.genericError'));
            }
        } catch (err) {
            setError(err.response?.data?.error || t('gym.firstLoginSetup.genericError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center px-4 py-10 sm:py-12 transition-colors"
            style={shellStyle}
        >
            <div className="w-full max-w-md rounded-2xl bg-white p-8 sm:p-10 shadow-xl shadow-black/20 border border-gray-100">
                <div className="flex justify-center w-full mb-2">
                    <GymLogo
                        variant="full"
                        className="!h-[4.25rem] sm:!h-[5rem] w-auto !max-w-[min(100%,300px)] shrink-0 text-[#372d83]"
                    />
                </div>

                <div className="flex items-center justify-center gap-2 text-[#372d83] mb-2">
                    <KeyRound size={22} className="shrink-0" />
                    <h1 className="text-xl font-bold text-gray-900">
                        {t('gym.firstLoginSetup.title')}
                    </h1>
                </div>

                <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
                    {t('gym.firstLoginSetup.description')}
                </p>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-600">
                            {t('gym.firstLoginSetup.newPassword')}
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                className={`${inputClass} pr-12`}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#372d83]/30"
                                tabIndex={-1}
                                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                            >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-600">
                            {t('gym.firstLoginSetup.confirmPassword')}
                        </label>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            className={inputClass}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center items-center rounded-xl py-3.5 px-4 font-semibold text-white bg-gradient-to-r from-[#372d83] to-[#5c4bb0] hover:from-[#2d246c] hover:to-[#4a3d96] shadow-md shadow-[#372d83]/25 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#372d83] disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                    >
                        {loading ? (
                            <LoadingSpinner size="small" />
                        ) : (
                            <span className="flex items-center gap-2">
                                {t('gym.firstLoginSetup.submit')}
                                <ArrowRight className="h-4 w-4" />
                            </span>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
